import { useEffect, useRef } from 'react';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { IDetailsOrderData } from 'types/Order.types';
import {
  Container,
  FormikContainer,
  Row,
  SelectWrapper,
  StyledSelect,
} from './CustomAddressCard.styled';
import { useTranslation } from 'react-i18next';
import { Formik, FormikProps } from 'formik';
import { IShippingAddressDTO } from '../types';
import CustomSelect from 'components/Select/FormikSelect';
import { useGetAllowedDeliveryCountryOptions } from 'utils/hooks/useGetAllowedDeliveryCountryOptions';
import { IOption } from 'components/Select/type';
import { Input } from 'components/Input/InputFormik';
import { customAddressSchema } from './validation';
import FormObserver from './FormObserver';
import { deliveryTypeOptions } from './constants';

interface FormValues {
  country: string;
  city: string;
  code: string;
  street: string;
  number: string;
  contact: string | null;
  email: string | null;
  phone: string | null;
  deliveryType: string;
}

interface ICustomAddressCardProps {
  setCustomAddressValid: (valid: boolean) => void;
  allowedDeliveryCountries: string[] | null;
  // Owned by CheckoutPage, which knows whether the details live in orderInfo or
  // in a per-supplier slot. Reading redux directly here would write the address
  // into the wrong place in the quotation -> order flow.
  value: IDetailsOrderData;
  onChange: (detailsOrderData: IDetailsOrderData) => void;
}

const CustomAddressCard = ({
  setCustomAddressValid,
  allowedDeliveryCountries,
  value: detailsOrderData,
  onChange,
}: ICustomAddressCardProps) => {
  const { t, i18n } = useTranslation();
  const formikRef = useRef<FormikProps<FormValues> | null>(null);

  useEffect(() => {
    formikRef.current?.validateForm();
  }, [i18n.language]);

  const { isTablet, isSmallTablet } = useBreakpointFlag();
  const countryOptions: IOption[] = useGetAllowedDeliveryCountryOptions(
    allowedDeliveryCountries
  );
  const updateOrderData = (formValues: FormValues) => {
    const dto: IShippingAddressDTO = {
      contact_person: formValues.contact,
      payment_method: formValues.deliveryType,
      email: formValues.email,
      phone_number: formValues.phone,
      address: {
        street: formValues.street,
        city: formValues.city,
        zip_code: formValues.code,
        house_number: formValues.number,
        country: formValues.country,
      },
    };

    if (
      JSON.stringify(detailsOrderData.shipping_address_data) !==
      JSON.stringify(dto)
    ) {
      onChange({
        ...detailsOrderData,
        shipping_address_data: dto,
      });
    }
  };

  return (
    <Container>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={{
          country: '',
          city: '',
          code: '',
          street: '',
          number: '',
          contact: null,
          email: null,
          phone: null,
          deliveryType: '',
        }}
        validationSchema={customAddressSchema(t)}
        validateOnChange={true}
        validateOnBlur={true}
        validateOnMount={true}
        onSubmit={updateOrderData}
      >
        {({ handleBlur, setFieldValue, errors, touched }) => {
          return (
            <FormikContainer>
              <FormObserver
                setCustomAddressValid={setCustomAddressValid}
                updateOrderData={updateOrderData}
              />
              <Row>
                <Input
                  errorMessage={touched['contact'] ? errors['contact'] : ''}
                  height={'41rem'}
                  name="contact"
                  placeholder={t('Contact person')}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    setFieldValue('contact', !value.length ? null : value);
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <Input
                  errorMessage={touched['email'] ? errors['email'] : ''}
                  height={'41rem'}
                  name="email"
                  placeholder={t('Email')}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    setFieldValue('email', !value.length ? null : value);
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <Input
                  errorMessage={touched['phone'] ? errors['phone'] : ''}
                  height={'41rem'}
                  name="phone"
                  placeholder={t('Phone number')}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    setFieldValue('phone', !value.length ? null : value);
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />
                <SelectWrapper>
                  <StyledSelect
                    errorMessage={
                      touched['deliveryType'] ? errors['deliveryType'] : ''
                    }
                    height={'41rem'}
                    name="deliveryType"
                    options={deliveryTypeOptions}
                    placeholder={t('Delivery type')}
                    component={CustomSelect}
                    isMulti={false}
                    onSelect={(value: string) => {
                      setFieldValue('deliveryType', value);
                    }}
                  />
                </SelectWrapper>
              </Row>

              <Row>
                <SelectWrapper>
                  <StyledSelect
                    errorMessage={touched['country'] ? errors['country'] : ''}
                    height={'41rem'}
                    name="country"
                    options={countryOptions}
                    component={CustomSelect}
                    placeholder={t('Country')}
                    isMulti={false}
                    onSelect={(value: string) => {
                      setFieldValue('country', value);
                    }}
                  />
                </SelectWrapper>

                <Input
                  errorMessage={touched['city'] ? errors['city'] : ''}
                  height={'41rem'}
                  name="city"
                  placeholder={t('City')}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    setFieldValue('city', value);
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <Input
                  errorMessage={touched['code'] ? errors['code'] : ''}
                  height={'41rem'}
                  name="code"
                  placeholder={t('Postal code')}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    const value = e.target.value.trim();
                    setFieldValue('code', value);
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <Input
                  errorMessage={touched['street'] ? errors['street'] : ''}
                  height={'41rem'}
                  name="street"
                  placeholder={t('Street')}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    setFieldValue('street', value);
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />

                <Input
                  errorMessage={touched['number'] ? errors['number'] : ''}
                  height={'41rem'}
                  name="number"
                  placeholder={t('Number')}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    setFieldValue('number', value);
                    handleBlur(e);
                  }}
                  wrapperStyles={{
                    width: isSmallTablet ? '100%' : isTablet ? '30%' : '100%',
                  }}
                />
              </Row>
            </FormikContainer>
          );
        }}
      </Formik>
    </Container>
  );
};

export default CustomAddressCard;
